import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, PackagePlus, Cog, Boxes, ClipboardList,
  Truck, AlertTriangle, Receipt, Users, FileText, History, BarChart3, LogOut, Menu, X,
  TreePine, MapPin, Coins, Ship, Send, Settings2, KeyRound,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { can, api } from "@/lib/api";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard:read" },
  { to: "/receiving", label: "Receiving", icon: PackagePlus, perm: "receiving:read" },
  { to: "/processing", label: "Processing", icon: Cog, perm: "processing:read" },
  { to: "/stock", label: "Stock on Hand", icon: Boxes, perm: "stock:read" },
  { to: "/offsite", label: "Offsite Stock", icon: MapPin, perm: "offsite:read" },
  { to: "/orders", label: "Orders", icon: ClipboardList, perm: "orders:read" },
  { to: "/dispatch", label: "Dispatch", icon: Truck, perm: "dispatch:read" },
  { to: "/shipments", label: "Shipments", icon: Ship, perm: "shipments:read" },
  { to: "/damages", label: "Damages", icon: AlertTriangle, perm: "damages:read" },
  { to: "/finance", label: "Finance", icon: Receipt, perm: "finance:read" },
  { to: "/cost-dashboard", label: "Cost Dashboard", icon: Coins, perm: "costdash:read" },
  { to: "/master-data", label: "Master Data", icon: Users, perm: "master:read" },
  { to: "/documents", label: "Documents", icon: FileText, perm: "documents:read" },
  { to: "/reports", label: "Reports", icon: BarChart3, perm: "reports:read" },
  { to: "/email", label: "Email Center", icon: Send, perm: "email:read" },
  { to: "/audit", label: "Audit Trail", icon: History, perm: "audit:read" },
  { to: "/settings", label: "Settings", icon: Settings2, perm: "settings:manage" },
];

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [secretAlert, setSecretAlert] = useState(null);

  useEffect(() => {
    api.get("/mailer/secret-status").then((r) => {
      if (r.data.level !== "ok") setSecretAlert(r.data);
    }).catch(() => {});
  }, []);

  const role = user?.role || "viewer";
  const navItems = NAV.filter((n) => can(role, n.perm));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-stone-50">
      {/* Mobile toggle */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="md:hidden fixed top-3 left-3 z-50 p-2 rounded-sm bg-stone-950 text-white"
        data-testid="sidebar-toggle"
      >
        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed md:static z-40 top-0 left-0 h-screen w-64 bg-stone-950 text-stone-300 transform transition-transform ${
          open ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
        data-testid="app-sidebar"
      >
        <div className="px-5 py-5 border-b border-stone-800 flex items-center gap-2">
          <TreePine className="h-6 w-6 text-emerald-400" />
          <div>
            <div className="font-display text-lg font-bold tracking-tight text-white">Forestry WMS</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500">Stock & Finance</div>
          </div>
        </div>

        <nav className="px-2 py-3 overflow-y-auto" style={{ maxHeight: "calc(100vh - 200px)" }}>
          {navItems.map((n) => {
            const Icon = n.icon;
            const active = location.pathname === n.to || (n.to !== "/" && location.pathname.startsWith(n.to));
            return (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 mb-0.5 rounded-sm text-sm transition-colors ${
                  active
                    ? "bg-stone-900 text-white border-l-2 border-emerald-500 pl-[10px]"
                    : "text-stone-400 hover:text-white hover:bg-stone-900/60"
                }`}
                data-testid={`nav-${n.to.replace("/", "") || "dashboard"}`}
              >
                <Icon className="h-4 w-4" />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 px-4 py-3 border-t border-stone-800 bg-stone-950">
          <div className="text-xs">
            <div className="text-white font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-stone-500 uppercase tracking-wider text-[10px]" data-testid="sidebar-user-role">{user?.role?.replace(/_/g, " ")}</div>
          </div>
          <button
            type="button"
            onClick={() => { setOpen(false); navigate("/change-password"); }}
            className="mt-2 w-full flex items-center gap-2 text-stone-400 hover:text-white text-xs"
            data-testid="change-password-link"
          >
            <KeyRound className="h-3.5 w-3.5" /> Change my password
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="mt-1.5 w-full flex items-center gap-2 text-stone-400 hover:text-white text-xs"
            data-testid="logout-btn"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 md:ml-0 min-w-0">
        {secretAlert && (
          <div className={`px-4 py-2 text-sm text-center font-medium ${secretAlert.level === "expired" ? "bg-red-600 text-white" : "bg-amber-400 text-stone-900"}`}
               data-testid="secret-alert-banner">
            {secretAlert.level === "expired"
              ? `Email sending is DOWN: the Microsoft mail client secret expired on ${secretAlert.expiry}. Renew it in Entra and update the system.`
              : `Heads up: the Microsoft mail client secret expires in ${secretAlert.days_left} day${secretAlert.days_left === 1 ? "" : "s"} (${secretAlert.expiry}). Renew it soon — see Email Center.`}
          </div>
        )}
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {children}
        </div>
      </main>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-stone-950">{title}</h1>
        {subtitle && <div className="mt-1 text-sm text-stone-500">{subtitle}</div>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
